import * as React from 'react';
import * as ReactDOM from 'react-dom';
import EmptyState, { EmptyStateProps } from 'owa-empty-state/lib/components/EmptyState';
import { ControlIcons } from 'owa-control-icons';
import { FocusZone } from 'office-ui-fabric-react/lib/FocusZone';
import { FolderBreadcrumb } from '../store/schema/FolderBreadcrumb';
import { FolderSelector, FolderSelectorProps } from './FolderSelector';
import { FolderSelectorBottomPart } from './FolderSelectorBottomPart';
import { FolderSelectorProperties } from '../types/FolderSelectorProperties';
import { FolderSelectorResult } from '../types/FolderSelectorResult';
import { FolderSelectorTopPart } from './FolderSelectorTopPart';
import { format } from 'owa-strings';
import { getErrorEmptyStateProps, isLoadingFolderRelatedError } from '../selectors/errorSelectors';
import { getOwaCommonStrings } from 'owa-strings/owa-common-strings';
import { getSelectedProvider } from '../selectors/selectedProviderSelectors';
import { getSelectedProviderType } from '../selectors/selectedProviderSelectors';
import { getTopBreadcrumbName, isTopBreadcrumbRoot } from '../selectors/breadcrumbsSelectors';
import { getUserSelectedFolderIdForAction } from '../selectors/selectedFolderSelectors';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { isCreatingNewFolder } from '../selectors/createNewFolderSelectors';
import { isEmptyListOfFolders } from '../selectors/folderModelSelectors';
import { isFolderSelectorOpen } from '../selectors/folderSelectorViewSelectors';
import { isLoading } from '../selectors/loadingSelectors';
import { LoadingView } from './LoadingView';
import { logUsage } from 'owa-analytics';
import { Modal } from 'office-ui-fabric-react/lib/Modal';
import { NewFolderField } from './NewFolderField';
import { observer } from 'mobx-react';
import {
    breadcrumbSelected,
    closeFolderSelector,
    folderSelected,
    openFolderSelector,
} from '../actions/internalActions';

const styles = require('./FileProviderFolderSelectorModal.scss');

interface FolderSelectorContentProps extends Pick<FolderSelectorProps, 'onFolderSelected'> {}

@observer
class FolderSelectorContent extends React.Component<FolderSelectorContentProps, {}> {
    render() {
        const emptyState = this.getEmptyStateIfApplicable();
        const { onFolderSelected } = this.props;

        return (
            emptyState || (
                <div className={styles.folderSelectorContainer}>
                    <FolderSelector onFolderSelected={onFolderSelected} />
                </div>
            )
        );
    }

    private getEmptyStateIfApplicable(): JSX.Element | null {
        let emptyStateProps: EmptyStateProps | null = null;
        if (isLoadingFolderRelatedError()) {
            emptyStateProps = getErrorEmptyStateProps();
        } else if (isEmptyListOfFolders() && getTopBreadcrumbName()) {
            const message = isTopBreadcrumbRoot()
                ? getOwaCommonStrings().noFoldersInRootText
                : format(getOwaCommonStrings().uploadToFolderText, getTopBreadcrumbName());

            emptyStateProps = {
                icon: require('owa-empty-state/lib/svg/colored/files.svg'),
                mainMessage: message,
            };
        }

        return emptyStateProps ? (
            <div className={styles.emptyStateContainer}>
                <EmptyState
                    {...emptyStateProps}
                    classNames={{
                        container: styles.emptyStateInnerContainer,
                        mainMessage: styles.emptyStateMessage,
                    }}
                />
            </div>
        ) : null;
    }
}

interface FileProviderFolderSelectorPanelProps {
    onFolderSelectedCallback: (result: FolderSelectorResult) => void;
    onErrorCallback: (error: Error) => void;
    properties: FolderSelectorProperties;
}

const enum UserClickAction {
    PrimaryButton,
    Dismiss,
}

@observer
class FileProviderFolderSelectorModal extends React.Component<
    FileProviderFolderSelectorPanelProps,
    {}
> {
    private userClickAction: UserClickAction | null = null;

    render() {
        return (
            <Modal
                isOpen={isFolderSelectorOpen()}
                onDismiss={this.dimissModal}
                onDismissed={this.onModalDismissed}>
                {this.renderFolderSelector()}
            </Modal>
        );
    }

    private renderFolderSelector() {
        const {
            properties: { titleString },
        } = this.props;

        return (
            <div className={styles.modalContainer}>
                <Icon
                    className={styles.closeIcon}
                    iconName={ControlIcons.Cancel}
                    onClick={this.dimissModal}
                />
                <div className={styles.headerContainer}>
                    <FolderSelectorTopPart
                        onBreadcrumbSelected={this.onBreadcrumbSelected}
                        headerTitle={titleString}
                    />
                </div>
                {this.renderFolderSelectorContent()}
                <div className={styles.bottomContainer}>{this.renderBottomContainerContent()}</div>
            </div>
        );
    }

    private renderFolderSelectorContent() {
        return this.shouldShowLoadingView() ? (
            <div className={styles.loadingViewContainer}>
                <LoadingView />
            </div>
        ) : (
            <>
                <div className={styles.divider} />
                <FolderSelectorContent onFolderSelected={this.onFolderSelected} />
                <div className={styles.divider} />
            </>
        );
    }

    private renderBottomContainerContent() {
        const {
            properties: { primaryButtonString },
        } = this.props;
        const shouldShowNewFolder = getSelectedProvider() !== null && !this.shouldShowLoadingView();

        return (
            <>
                <div className={styles.newFolderContainer}>
                    {shouldShowNewFolder && (
                        <FocusZone>
                            <NewFolderField />
                        </FocusZone>
                    )}
                </div>
                <FolderSelectorBottomPart
                    onPrimaryButtonClicked={this.primaryButtonClicked}
                    onCancelButtonClicked={this.dimissModal}
                    primaryButtonString={primaryButtonString}
                />
            </>
        );
    }

    private shouldShowLoadingView(): boolean {
        return isLoading() || isCreatingNewFolder();
    }

    private onFolderSelected = (folderKey: string) => {
        folderSelected(folderKey);
    };

    private onBreadcrumbSelected = (selectedBreadcrumb: FolderBreadcrumb) => {
        breadcrumbSelected(selectedBreadcrumb);
    };

    private closeModal = () => {
        closeFolderSelector();
    };

    private primaryButtonClicked = () => {
        this.userClickAction = UserClickAction.PrimaryButton;
        this.closeModal();
    };

    private dimissModal = () => {
        this.userClickAction = UserClickAction.Dismiss;
        this.closeModal();
    };

    private triggerFolderSelectedCallback() {
        const folderId = getUserSelectedFolderIdForAction();

        if (folderId) {
            this.props.onFolderSelectedCallback({
                isFolderSelected: true,
                providerType: getSelectedProviderType(),
                folderId: folderId,
            });
        } else {
            // We should generally not reach this state as among the selected folder
            // the root folder would always be part of it.
            this.props.onErrorCallback(
                new Error(
                    'Not able to figure out any folder that user has selected for the primary action'
                )
            );
        }
    }

    private onModalDismissed = () => {
        if (this.userClickAction === UserClickAction.PrimaryButton) {
            logUsage('FPFolderSelectorUpload');
            this.triggerFolderSelectedCallback();
        } else {
            logUsage('FPFolderSelectorDismissed');
            this.props.onFolderSelectedCallback({ isFolderSelected: false });
        }

        // When the modal is dismissed we reset the click action
        this.userClickAction = null;
    };
}

export const FILE_PROVIDER_FOLDER_SELECTOR_DIV_ID = 'fileProviderFolderSelector';
export function showFileProviderFolderSelector(
    properties: FolderSelectorProperties
): Promise<FolderSelectorResult> {
    let folderSelectorDiv = document.getElementById(FILE_PROVIDER_FOLDER_SELECTOR_DIV_ID);
    if (!folderSelectorDiv) {
        // As we can have only one folder selector open at a time so we only create the
        // DOM element once and reuse that.
        folderSelectorDiv = document.createElement('div');
        folderSelectorDiv.id = FILE_PROVIDER_FOLDER_SELECTOR_DIV_ID;
        document.body.appendChild(folderSelectorDiv);
    }

    /* tslint:disable:promise-must-complete */
    return new Promise<FolderSelectorResult>((resolve, reject) => {
        openFolderSelector();

        ReactDOM.render(
            <FileProviderFolderSelectorModal
                onFolderSelectedCallback={resolve}
                onErrorCallback={reject}
                properties={properties}
            />,
            folderSelectorDiv
        );
    });
    /* tslint:enable:promise-must-complete */
}
